import React, { useState, useEffect } from 'react';
import { db, storage } from '../firebase';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { useNavigate, Link } from 'react-router-dom';
import './styles/adminReferences.css';
import adminHero from '../images/image1.jpg';
import { FaEdit, FaCopy, FaTrash } from 'react-icons/fa';

function AdminReferences() {
  const navigate = useNavigate();
  const [references, setReferences] = useState([]);
  const [user, setUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  
  // Reference data
  const [reference, setReference] = useState({
    title: '',
    description: '',
    imageUrl: '',
    logoUrl: '',
    cvr: '',
    website: '',
    slettetDato: null,
    erSlettet: false
  });
  
  // Upload files
  const [image, setImage] = useState(null);
  const [logo, setLogo] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [editingReference, setEditingReference] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        hentReferences();
      } else {
        navigate('/login'); // Redirect til login hvis ikke logget ind
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const hentReferences = async () => {
    try {
      const referencesCollection = collection(db, 'references');
      const data = await getDocs(referencesCollection);
      const alleReferences = data.docs.map(doc => ({ ...doc.data(), id: doc.id }));
      
      // Filtrer slettede referencer, hvis de er blevet slettet for mere end 7 dage siden
      const aktiveReferences = alleReferences.filter(ref => {
        if (ref.erSlettet) {
          const slettetDato = new Date(ref.slettetDato);
          const nuDato = new Date();
          const dageForskel = Math.floor((nuDato - slettetDato) / (1000 * 60 * 60 * 24));
          return dageForskel < 7;
        }
        return !ref.erSlettet;
      });
      
      setReferences(aktiveReferences);
    } catch (error) {
      console.error("Fejl ved hentning af referencer:", error);
      alert('Fejl ved hentning af referencer: ' + error.message);
    }
  };

  const handleEdit = (ref) => {
    setEditingReference(ref);
    setReference(ref);
    setImagePreview(ref.imageUrl);
    setLogoPreview(ref.logoUrl);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setIsLoading(true);
    setLoadingProgress(0);
    
    const loadingInterval = setInterval(() => {
      setLoadingProgress(prev => {
        const newProgress = prev >= 50 ? prev + 0.2 :
                          prev >= 25 ? prev + 0.5 :
                          prev + 1;
        return Math.min(newProgress, 99.9);
      });
    }, 50);

    try {
      const referenceData = { ...reference };
      
      // Upload billede hvis nyt er valgt
      if (image) {
        const imagePath = `references/images/${Date.now()}_${image.name}`;
        const imageRef = ref(storage, imagePath);
        await uploadBytes(imageRef, image);
        setLoadingProgress(40);
        const imageUrl = await getDownloadURL(imageRef);
        referenceData.imageUrl = imageUrl;
      }
      
      // Upload logo hvis nyt er valgt
      if (logo) {
        const logoPath = `references/logos/${Date.now()}_${logo.name}`;
        const logoRef = ref(storage, logoPath);
        await uploadBytes(logoRef, logo);
        setLoadingProgress(70);
        const logoUrl = await getDownloadURL(logoRef);
        referenceData.logoUrl = logoUrl;
      }

      if (editingReference) {
        // Opdater eksisterende reference
        const referenceRef = doc(db, 'references', editingReference.id);
        await updateDoc(referenceRef, referenceData);
      } else {
        // Tilføj en timestamp ved oprettelse
        referenceData.createdAt = new Date().toISOString();
        await addDoc(collection(db, 'references'), referenceData);
      }

      clearInterval(loadingInterval);
      setLoadingProgress(100);
      
      setTimeout(() => {
        resetForm();
        hentReferences();
        setShowModal(false);
        setIsLoading(false);
      }, 500);

    } catch (error) {
      clearInterval(loadingInterval);
      setIsLoading(false);
      alert('Fejl: ' + error.message);
    }
  };
  
  const resetForm = () => {
    setReference({ 
      title: '', 
      description: '', 
      imageUrl: '', 
      logoUrl: '',
      cvr: '',
      website: '', 
      slettetDato: null, 
      erSlettet: false 
    });
    setImage(null);
    setLogo(null);
    setImagePreview(null);
    setLogoPreview(null);
    setEditingReference(null);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Er du sikker på at du vil slette denne reference? Du har 7 dage til at fortryde.')) {
      try {
        const referenceRef = doc(db, 'references', id);
        await updateDoc(referenceRef, {
          erSlettet: true,
          slettetDato: new Date().toISOString()
        });
        
        hentReferences();
      } catch (error) {
        alert('Fejl ved sletning: ' + error.message);
      }
    }
  };

  const fortrydSletning = async (ref) => {
    try {
      const referenceRef = doc(db, 'references', ref.id);
      await updateDoc(referenceRef, {
        erSlettet: false,
        slettetDato: null
      });
      hentReferences();
    } catch (error) {
      alert('Fejl ved fortrydelse af sletning: ' + error.message);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogo(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCopy = (refToCopy) => {
    const kopiTitle = `${refToCopy.title}-kopi`;
    setReference({
      ...refToCopy,
      title: kopiTitle
    });
    setImagePreview(refToCopy.imageUrl);
    setLogoPreview(refToCopy.logoUrl);
    setShowModal(true);
    setEditingReference(null); // Sikrer at vi opretter ny reference og ikke redigerer
  };

  if (!user) return null; // Returner intet hvis ikke logget ind

  return (
    <>
      <div className="adminref-hero">
        <div className="adminref-hero-overlay"></div>
        <img src={adminHero} alt="Administration" className="adminref-hero-image" />
      </div>

      <div className="adminref-container">
        <div className="adminref-header">
          <Link to="/admin" className="adminref-back-button">
            ← Tilbage til admin
          </Link>
          <h1>Referencer og Cases</h1>
        </div>

        <div className="adminref-grid">
          <div 
            className="adminref-create-card"
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
          >
            <div className="adminref-create-icon">+</div>
            <div className="adminref-create-text">Opret ny reference</div>
          </div>
          
          {references.map((ref) => (
            <div key={ref.id} className={`adminref-case-item ${ref.erSlettet ? 'slettet' : ''}`}>
              {ref.erSlettet && (
                <div className="adminref-slettet-banner">
                  <p>Denne reference bliver slettet om {7 - Math.floor((new Date() - new Date(ref.slettetDato)) / (1000 * 60 * 60 * 24))} dage</p>
                  <button onClick={() => fortrydSletning(ref)} className="adminref-fortryd-btn">
                    Fortryd Sletning
                  </button>
                </div>
              )}
              <div className="adminref-image-container">
                <img src={ref.imageUrl || adminHero} alt={ref.title} className="case-image" />
                {ref.logoUrl && (
                  <div className="adminref-case-logo">
                    <img src={ref.logoUrl} alt={`${ref.title} logo`} />
                  </div>
                )}
              </div>
              <div className="adminref-case-content">
                <h3>{ref.title}</h3>
                <p>{ref.description}</p>
                {ref.cvr && <p className="adminref-case-meta">CVR: {ref.cvr}</p>}
                {ref.website && <p className="adminref-case-meta">Website: {ref.website}</p>}
              </div>
              <div className="adminref-case-actions">
                <button 
                  onClick={() => handleEdit(ref)} 
                  className="adminref-edit-btn"
                  title="Rediger"
                >
                  <FaEdit />
                </button>
                <button 
                  onClick={() => handleCopy(ref)} 
                  className="adminref-copy-btn"
                  title="Kopier"
                >
                  <FaCopy />
                </button>
                <button 
                  onClick={() => handleDelete(ref.id)}
                  className="adminref-delete-btn"
                  title="Slet"
                >
                  <FaTrash />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {showModal && (
        <div className="adminref-modal">
          <div className="adminref-modal-content">
            <span className="adminref-close" onClick={() => setShowModal(false)}>&times;</span>
            <h2>{editingReference ? 'Rediger Reference' : 'Opret Ny Reference'}</h2>
            
            {isLoading && (
              <>
                <div className="adminref-loading-overlay"></div>
                <div className="adminref-loading-container">
                  <div className="adminref-loading-bar">
                    <div 
                      className="adminref-loading-progress" 
                      style={{ width: `${loadingProgress}%` }}
                    ></div>
                  </div>
                  <div className="adminref-loading-text">
                    {loadingProgress.toFixed(0)}%
                  </div>
                </div>
              </>
            )}

            <form onSubmit={handleSubmit}>
              <div className="adminref-form-group">
                <label>Titel:</label>
                <input
                  type="text"
                  value={reference.title}
                  onChange={(e) => setReference({...reference, title: e.target.value})}
                  required
                />
              </div>

              <div className="adminref-form-group">
                <label>Beskrivelse:</label>
                <textarea
                  value={reference.description}
                  onChange={(e) => setReference({...reference, description: e.target.value})}
                  required
                  rows={6}
                />
              </div>

              <div className="adminref-form-row">
                <div className="adminref-form-group">
                  <label>CVR-nummer:</label>
                  <input
                    type="text"
                    value={reference.cvr || ''}
                    onChange={(e) => setReference({...reference, cvr: e.target.value})}
                    placeholder="F.eks. 12345678"
                  />
                </div>

                <div className="adminref-form-group">
                  <label>Hjemmeside URL:</label>
                  <input
                    type="url"
                    value={reference.website || ''}
                    onChange={(e) => setReference({...reference, website: e.target.value})}
                    placeholder="F.eks. https://www.example.com"
                  />
                </div>
              </div>

              <div className="adminref-form-group">
                <label>Billeder:</label>
                <div className="adminref-image-upload">
                  <div>
                    <label htmlFor="image-input">
                      <div className="adminref-image-preview">
                        {imagePreview ? (
                          <img 
                            src={imagePreview} 
                            alt="Preview" 
                          />
                        ) : (
                          <div className="adminref-image-placeholder">
                            <span>+</span>
                            <p>Vælg case-billede</p>
                          </div>
                        )}
                      </div>
                    </label>
                    <input
                      id="image-input"
                      type="file"
                      onChange={handleImageChange}
                      accept="image/*"
                      className="adminref-image-input"
                    />
                  </div>

                  <div>
                    <label htmlFor="logo-input">
                      <div className="adminref-logo-preview">
                        {logoPreview ? (
                          <img 
                            src={logoPreview} 
                            alt="Logo Preview" 
                          />
                        ) : (
                          <div className="adminref-image-placeholder">
                            <span>+</span>
                            <p>Vælg logo</p>
                          </div>
                        )}
                      </div>
                    </label>
                    <input
                      id="logo-input"
                      type="file"
                      onChange={handleLogoChange}
                      accept="image/*"
                      className="adminref-logo-input"
                    />
                  </div>
                </div>
              </div>

              <button type="submit" className="adminref-submit-btn">
                {editingReference ? 'Opdater' : 'Opret'} Reference
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export default AdminReferences;
